import { IAlertProps } from "@components/alert/Alert.types";
import { isValidDate } from "@components/inputs/date/utils";
import { getInfoValue, IFieldDef, isVisibleByPath, TGetValueFn } from "@components/smart/FieldInfo";
import {
    ActionType,
    addEmptyLineItem,
    addNewLineItem,
    ISmartFastEntriesActionEvent
} from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { CustomerPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    EntitySetName,
    EntityTypeName,
    IPrCaregivingEventEntity,
    IPrCareReceiverSocialEventEntity,
    IPrMaternityLeaveEventEntity,
    IPrSocialEventEntity,
    IPrSocialEventMonthDetailEntity,
    IPrSocialEventMonthIntervalEntity,
    PrCaregivingEventEntity,
    PrEmployeeEntity,
    PrIllnessLeaveEventEntity,
    PrMaternityLeaveEventEntity,
    PrPaternityLeaveEventEntity,
    PrSocialEventEntity,
    PrSocialEventMonthDetailEntity,
    PrSocialEventMonthIntervalEntity,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrSocialEventMonthDetailTypeCode,
    PrSocialEventStatusCode,
    PrSocialEventTypeCode,
    PrSubmissionTypeCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { tWithFallback } from "@pages/documents/Document.utils";
import { getUserId, IdentificationTypeLocalPath } from "@pages/payroll/employee/Employee.utils";
import { createPeriodFromDate } from "@pages/payroll/payroll.utils";
import { SocialEventGroup } from "@pages/payroll/socialEvents/SocialEvents.types";
import { isEndedEvent, MonthDetailIntervalsFastEntryDef } from "@pages/payroll/socialEvents/SocialEventsDef";
import { Dayjs } from "dayjs";
import i18next from "i18next";

import { REST_API_URL } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { isCustomerSession } from "../../../contexts/appContext/AppContext.utils";
import { Model } from "../../../model/Model";
import { StorageModel } from "../../../model/StorageModel";
import { ITableStorageDefaultCustomData } from "../../../model/TableStorage";
import { Theme } from "../../../theme";
import DateType, {
    DateFormat,
    getUtcDateBy,
    getUtcDayjs,
    getUtcDayjsBy,
    isDateMonthAndYearBefore
} from "../../../types/Date";
import customFetch from "../../../utils/customFetch";
import { FormStorage, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { TCustomFilterFn } from "../../../views/table/TableView.utils";

export const hasDurationIntervalsLocalPath = BindingContext.localContext("hasDurationIntervals");
export const dateEventEndLocalPath = BindingContext.localContext(PrSocialEventEntity.DateEventEnd);
export const workedHoursOnLastDayLocalPath = BindingContext.localContext(PrSocialEventEntity.WorkedHoursOnLastDay);


export interface ISocialEventsTableCustomData extends ITableStorageDefaultCustomData {
}

export interface ISocialEventsFormCustomData extends IFormStorageDefaultCustomData {
    type: PrSocialEventTypeCode;
    isEndDialogOpened?: boolean;
    endDialogAlert?: IAlertProps;
    submissionType?: PrSubmissionTypeCode;
}

export type TSocialEventFormStorage = FormStorage<IPrSocialEventEntity, ISocialEventsFormCustomData>;

export const PrSocialEventsEntitySets = [
    EntitySetName.PrIllnessLeaveEvents,
    EntitySetName.PrCaregivingEvents,
    EntitySetName.PrLongTermCaregivingEvents,
    EntitySetName.PrMaternityLeaveEvents,
    EntitySetName.PrPaternityLeaveEvents,
    EntitySetName.PrParentalLeaveEvents,
    EntitySetName.PrCustodyEvents,
    EntitySetName.PrImprisonmentEvents
] as const;

export function getTypeCodeFrom(entitySet: EntitySetName): PrSocialEventTypeCode {
    switch (entitySet) {
        case EntitySetName.PrCaregivingEvents:
            return PrSocialEventTypeCode.CaregivingAllowance;
        case EntitySetName.PrLongTermCaregivingEvents:
            return PrSocialEventTypeCode.LongtermCaregivingAllowance;
        case EntitySetName.PrMaternityLeaveEvents:
            return PrSocialEventTypeCode.MaternityLeave;
        case EntitySetName.PrPaternityLeaveEvents:
            return PrSocialEventTypeCode.PaternityLeave;
        case EntitySetName.PrIllnessLeaveEvents:
            return PrSocialEventTypeCode.IllnessLeave;
        case EntitySetName.PrParentalLeaveEvents:
            return PrSocialEventTypeCode.ParentalLeave;
        case EntitySetName.PrCustodyEvents:
            return PrSocialEventTypeCode.Custody;
        case EntitySetName.PrImprisonmentEvents:
            return PrSocialEventTypeCode.Imprisonment;
    }
    return null;
}

export function getIconNameFromEntitySet(entitySet: EntitySetName): string {
    switch (entitySet) {
        case EntitySetName.PrCaregivingEvents:
            return "Caregiving";
        case EntitySetName.PrLongTermCaregivingEvents:
            return "LongTermCaregiving";
        case EntitySetName.PrMaternityLeaveEvents:
            return "Maternity";
        case EntitySetName.PrPaternityLeaveEvents:
            return "Paternity";
        case EntitySetName.PrIllnessLeaveEvents:
            return "DPN";
        case EntitySetName.PrParentalLeaveEvents:
            return "Parental";
        case EntitySetName.PrCustodyEvents:
            return "Custody";
        case EntitySetName.PrImprisonmentEvents:
            return "Imprisonment";
        default:
            return null;
    }
}

export function getSocialEventsEntityType(type: PrSocialEventTypeCode): EntityTypeName {
    switch (type) {
        case PrSocialEventTypeCode.CaregivingAllowance:
            return EntityTypeName.PrCaregivingEvent;
        case PrSocialEventTypeCode.LongtermCaregivingAllowance:
            return EntityTypeName.PrLongTermCaregivingEvent;
        case PrSocialEventTypeCode.MaternityLeave:
            return EntityTypeName.PrMaternityLeaveEvent;
        case PrSocialEventTypeCode.PaternityLeave:
            return EntityTypeName.PrPaternityLeaveEvent;
        case PrSocialEventTypeCode.IllnessLeave:
            return EntityTypeName.PrIllnessLeaveEvent;
        case PrSocialEventTypeCode.Custody:
            return EntityTypeName.PrCustodyEvent;
        case PrSocialEventTypeCode.Imprisonment:
            return EntityTypeName.PrImprisonmentEvent;
        case PrSocialEventTypeCode.ParentalLeave:
            return EntityTypeName.PrParentalLeaveEvent;
    }
}

export function getEntitySetFromType(type: PrSocialEventTypeCode): EntitySetName {
    switch (type) {
        case PrSocialEventTypeCode.CaregivingAllowance:
            return EntitySetName.PrCaregivingEvents;
        case PrSocialEventTypeCode.LongtermCaregivingAllowance:
            return EntitySetName.PrLongTermCaregivingEvents;
        case PrSocialEventTypeCode.MaternityLeave:
            return EntitySetName.PrMaternityLeaveEvents;
        case PrSocialEventTypeCode.PaternityLeave:
            return EntitySetName.PrPaternityLeaveEvents;
        case PrSocialEventTypeCode.IllnessLeave:
            return EntitySetName.PrIllnessLeaveEvents;
        case PrSocialEventTypeCode.Custody:
            return EntitySetName.PrCustodyEvents;
        case PrSocialEventTypeCode.Imprisonment:
            return EntitySetName.PrImprisonmentEvents;
        case PrSocialEventTypeCode.ParentalLeave:
            return EntitySetName.PrParentalLeaveEvents;
    }
}

export const filterOutInvisibleDurationItems = (storage: FormStorage, _: string, items: IEntity[]): IEntity[] => {
    let endEvent = items.find(md => md.IsEnd);
    if (!endEvent) {
        const dateEventEnd = storage.getValueByPath(PrSocialEventEntity.DateEventEnd) as Date;
        endEvent = dateEventEnd && createPeriodFromDate(dateEventEnd);
    }
    const _isEnded = isEndedEvent({ storage });
    if (!endEvent) {
        return items;
    }
    return items.filter(md => md.Id !== endEvent.Id && (_isEnded ? isDateMonthAndYearBefore(md, endEvent) : true));
};

export const isDurationGroupToggled: TGetValueFn<boolean> = (args) => {
    return args.storage.getValueByPath(hasDurationIntervalsLocalPath);
};

export const isDurationGroupVisible: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    if (!isEndedEvent({ storage })) {
        // if event is not ended, show the group, so user can add duration intervals
        return true;
    }
    // if event is ended, there is always one item (the end item), and there can be also invisible duration items
    // -> show the group only if there is at least one visible duration item
    const items = storage.getValueByPath(PrSocialEventEntity.MonthDetails) as IPrSocialEventMonthDetailEntity[];
    const visibleItems = filterOutInvisibleDurationItems(storage as FormStorage, null, items);
    return !!visibleItems?.length;
};

export function defaultCareIntervalsAfterLoad(storage: FormStorage): void {
    const baseBc = storage.data.bindingContext.navigate(PrSocialEventEntity.MonthDetails);
    (storage.data.entity as IPrSocialEventEntity).MonthDetails?.forEach(md => {
        if (!md?.Intervals?.length && md?.MonthDetailTypeCode === PrSocialEventMonthDetailTypeCode.OnlyInSelectedDays) {
            addDefaultCareInterval(storage, baseBc.addKey(md).navigate(PrSocialEventMonthDetailEntity.Intervals));
        }
    });
}

export function addDefaultCareInterval(storage: FormStorage, intervalsBc: BindingContext): void {
    const md: IPrSocialEventMonthDetailEntity = storage.getValue(intervalsBc.getParent());
    const { DateEventStart } = (storage.data.entity ?? {}) as IPrCaregivingEventEntity;
    const monthDay = getMonthIntervalDay(md);
    let DateFrom: Date = null;
    if (DateEventStart && monthDay?.isSame(DateEventStart, "month")) {
        DateFrom = DateEventStart;
    }
    const defaultData: Partial<IPrSocialEventMonthIntervalEntity> = {
        DateFrom,
        DateTo: null,
    };
    addNewLineItem<IPrSocialEventMonthIntervalEntity>(storage, intervalsBc, MonthDetailIntervalsFastEntryDef, null, defaultData);
}

export function careGivingDetailTypeBeforeSave(entity: IPrCaregivingEventEntity): IEntity {
    entity.MonthDetails?.forEach(md => {
        if (md?.MonthDetailTypeCode === PrSocialEventMonthDetailTypeCode.WholeTimeOfAbsence) {
            md.Intervals = null;
        }
    });
    return entity;
}

export function careReceiverBeforeSave<E>(storage: FormStorage<E>, entity: IPrCaregivingEventEntity | IPrMaternityLeaveEventEntity): E {
    const hasCareReceiverCollection = !!storage.data.bindingContext.isValidNavigation(PrMaternityLeaveEventEntity.CareReceivers);
    const basePath = hasCareReceiverCollection ? PrMaternityLeaveEventEntity.CareReceivers : PrCaregivingEventEntity.CareReceiver;
    if (!isVisibleByPath(storage as unknown as FormStorage, createPath(basePath, IdentificationTypeLocalPath))) {
        if (hasCareReceiverCollection) {
            (entity as IPrMaternityLeaveEventEntity).CareReceivers = [];
        } else {
            (entity as IPrCaregivingEventEntity).CareReceiver = null;
        }
    }
    return entity as E;
}

interface IUploadError {
    Code: string;
    Message: string;
    FileName: string;
    NotificationId: string;
    NotificationType: string;
}

interface IUploadResponse {
    ProcessedCount: number;
    Errors: IUploadError[];
}

interface IUploadResult {
    successCount: number;
    errors: string[];
}

// handle DPN import from XML file
export async function uploadIllnessLeaveNotifications(files: File[]): Promise<IUploadResult> {
    try {
        const formData = new FormData();
        files.forEach(file => formData.append("file", file));

        const response = await customFetch(`${REST_API_URL}/PrSocialEvents/UploadIllnessLeaveNotifications`, {
            method: "POST",
            body: formData
        });

        if (response.ok) {
            const result = await response.json() as IUploadResponse;
            const errors: string[] = [];
            result.Errors?.forEach((err) => {
                errors.push(err.Message);
            });
            return { successCount: result.ProcessedCount, errors };
        } else {
            return { successCount: 0, errors: [i18next.t("Common:Errors.UnknownError")] };
        }
    } catch (e) {
        return { successCount: 0, errors: [e.message] };
    }
}

/**
 * Customer session with Attendance permission behaves like accountant
 */
export function hasCustomerAttendancePermission(context: IAppContext): boolean {
    return context.getCompanyPermissions().has(CustomerPermissionCodes.PrAttendance);
}

/**
 * Social Event Translation function, tries to find a translation key according to the user session
 *  - socialEvents.toml for accountants
 *  - socialRequests.toml for customer sessions with fallback to socialEvents.toml if key is not found
 * @param context
 * @param key
 */
export function seT(context: IAppContext, key: string): string {
    if (isCustomerSession(context) && !hasCustomerAttendancePermission(context)) {
        return tWithFallback("SocialRequests", key, "SocialEvents");
    }
    return i18next.t("SocialEvents:" + key);
}

export function getSocialEventTypeLabel(type: PrSocialEventTypeCode): string {
    if (i18next.exists(`SocialEvents:Types.${type}`)) {
        return i18next.t(`SocialEvents:Types.${type}`).toString();
    }
    return getEnumDisplayValue(EntityTypeName.PrSocialEventType, type);
}

export function getLastMonthOfEventContinues(entity: IPrSocialEventEntity): Date | null {
    let lastMonth: Date | null = null;
    entity.MonthDetails?.forEach(md => {
        const currDate = md.Month && md.Year ? getUtcDateBy(md.Year, md.Month - 1) : null;
        if (isValidDate(currDate) && (!lastMonth || getUtcDayjs(currDate).isAfter(lastMonth, "month"))) {
            lastMonth = currDate;
        }
    });
    return lastMonth;
}

export function getSocialEventStatusLabel(entity: IPrSocialEventEntity, withContinuesInfo?: boolean): string {
    const { SocialEventStatusCode } = entity;
    let suffix = "";
    if (withContinuesInfo) {
        const lastEventMonth = getLastMonthOfEventContinues(entity);
        suffix = SocialEventStatusCode === PrSocialEventStatusCode.Ongoing && lastEventMonth ? ` (${DateType.format(lastEventMonth, DateFormat.monthAndYearNumeric)})` : "";
    }
    const statusLabel = getEnumDisplayValue(EntityTypeName.PrSocialEventStatus, SocialEventStatusCode);
    return `${statusLabel}${suffix}`;
}

export function getSocialEventStatusColor(entity: IPrSocialEventEntity): keyof Theme {
    const { SocialEventStatusCode } = entity;
    switch (SocialEventStatusCode) {
        case PrSocialEventStatusCode.Completed:
            return "C_SEM_text_good";
        case PrSocialEventStatusCode.Cancelled:
            return "C_SEM_text_bad";
        default:
            return "C_SEM_text_warning";
    }
}

export enum SocialEventTabStatus {
    Active = "Active",
    Processed = "Processed",
}

export const getSingleEmployeeSocialEventFilter = (context: IAppContext): string => `${createPath(PrSocialEventEntity.Employee, PrEmployeeEntity.User, UserEntity.Id)} eq ${getUserId(context)}`;

export const canChangeEmployee: TGetValueFn<boolean> = (args) => {
    const context = args.context ?? args.storage.context;
    return !isCustomerSession(context) || hasCustomerAttendancePermission(context);
};

export function getFilterForTabId(context: IAppContext): TCustomFilterFn {
    const _canChangeEmployee = canChangeEmployee({ context });
    return (tabId: string, storage: StorageModel): string => {
        const filters: string[] = _canChangeEmployee ? [] : [getSingleEmployeeSocialEventFilter(context)];
        switch (tabId as SocialEventTabStatus) {
            case SocialEventTabStatus.Active:
                filters.push(`${PrSocialEventEntity.SocialEventStatusCode} ne '${PrSocialEventStatusCode.Cancelled}'`);
                filters.push(`${PrSocialEventEntity.IsProcessed} eq false`);
                break;
            case SocialEventTabStatus.Processed:
                filters.push(`${PrSocialEventEntity.SocialEventStatusCode} eq '${PrSocialEventStatusCode.Cancelled}' OR ${PrSocialEventEntity.IsProcessed} eq true`);
                break;
        }
        return filters.join(" AND ");
    };
}

export const isAllowedToCreateSubmission: TGetValueFn<boolean> = (args) => {
    return !isCustomerSession(args.context ?? args.storage.context);
};

export function initMonthDetailsAfterLoad(storage: TSocialEventFormStorage): void {
    storage.setValueByPath(hasDurationIntervalsLocalPath, !!storage.data.entity.MonthDetails?.some(md => !md.IsEnd));
}

export function getNewMonthDetailItem(storage: TSocialEventFormStorage): IPrSocialEventMonthDetailEntity {
    const groupDef = storage.data.definition.groups?.find(g => g.id === SocialEventGroup.MonthDetails);
    // create new lineItem with defaults based on event start date
    return addEmptyLineItem({
        storage,
        bindingContext: storage.data.bindingContext.navigate(PrSocialEventEntity.MonthDetails),
        newItemsCount: 1,
        index: 1,
        columns: groupDef?.lineItems?.columns ?? [],
        context: storage.context
    }) as IPrSocialEventMonthDetailEntity;
}

export function handleDurationIntervalToggle(storage: TSocialEventFormStorage, e: ISmartFieldChange): void {
    if (e.bindingContext.getPath() === hasDurationIntervalsLocalPath) {
        const hasIntervals = e.value as boolean;
        const values: IPrSocialEventMonthDetailEntity[] = [];
        if (hasIntervals) {
            const dateStart = storage.data.entity.DateEventStart;
            const detail = getNewMonthDetailItem(storage);
            detail.Month = dateStart.getMonth() + 1;
            detail.Year = dateStart.getFullYear();
            const dateContinuesBc = storage.data.bindingContext
                .navigate(PrSocialEventEntity.MonthDetails)
                .navigate(PrSocialEventMonthDetailEntity.DateEventContinues);
            const info = storage.getInfo(dateContinuesBc);
            // add date event continues if present on entity
            if (info) {
                detail.DateEventContinues = getDefaultDateContinues(storage, detail);
            }
            values.push(detail);
        }
        storage.setValueByPath(PrIllnessLeaveEventEntity.MonthDetails, values);
        storage.refreshGroupByKey(SocialEventGroup.MonthDetails);
    }
}

export function handleDurationIntervalLineItemsAction(storage: TSocialEventFormStorage, args: ISmartFastEntriesActionEvent): void {
    const path = args.bindingContext.getPath(true);
    if (path === PrSocialEventEntity.MonthDetails && args.actionType === ActionType.Add) {
        // fill correct data to the newly created item
        const [newItem] = args.affectedItems;
        const idx = args.items?.findIndex(item => item === newItem);
        if (idx < 1) {
            // just for sure, should not be the case, because there should be always one item when add button is visible
            return;
        }
        const previousItem = args.items?.[idx - 1];
        const shouldAddNextYearJanuary = previousItem.Month === 12;
        newItem.Month = shouldAddNextYearJanuary ? 1 : previousItem.Month + 1;
        newItem.Year = shouldAddNextYearJanuary ? previousItem.Year + 1 : previousItem.Year;
        const dateContinuesBc = args.bindingContext.navigate(PrSocialEventMonthDetailEntity.DateEventContinues);
        const info = storage.getInfo(dateContinuesBc);
        // add date event continues if present on entity
        if (info) {
            newItem.DateEventContinues = getDefaultDateContinues(storage, newItem);
        }
    }
}

export function prepareMonthDetailIntervalsAfterTypeChange(storage: FormStorage, intervalsBc: BindingContext, value: PrSocialEventMonthDetailTypeCode): void {
    if (value === PrSocialEventMonthDetailTypeCode.OnlyInSelectedDays) {
        // generate first interval
        addDefaultCareInterval(storage, intervalsBc);
    } else {
        storage.setValue(intervalsBc, []);
    }
}

export function handleDurationIntervalLineItemsChange(storage: TSocialEventFormStorage, args: ISmartFieldChange): void {
    const path = args.bindingContext.getPath(true);
    if (path === PrSocialEventMonthDetailEntity.MonthDetailType) {
        const bc = args.bindingContext.getParent().navigate(PrSocialEventMonthDetailEntity.Intervals);
        prepareMonthDetailIntervalsAfterTypeChange(storage, bc, args.value as PrSocialEventMonthDetailTypeCode);
        storage.refresh(true);
    }
    if ([PrSocialEventMonthIntervalEntity.DateFrom, PrSocialEventMonthIntervalEntity.DateTo].includes(path as PrSocialEventMonthIntervalEntity)) {
        storage.refresh(true);
    }
}

export function getMonthIntervalDay(md: IPrSocialEventMonthDetailEntity): Dayjs {
    const { Month, Year } = md ?? {};
    if (Month && Year) {
        return getUtcDayjsBy(Year, Month - 1);
    }
    return null;
}

export function getMonthIntervalDayStorage(storage: Model, bc: BindingContext): Dayjs {
    const md = storage.getValue(bc);
    return getMonthIntervalDay(md);
}

export function getDefaultDateContinues(storage: FormStorage, md?: IPrSocialEventMonthDetailEntity): Date {
    const monthDate = getMonthIntervalDay(md);
    const now = getUtcDayjs();
    const eventStart = storage.data.entity.DateEventStart ? getUtcDayjs(storage.data.entity.DateEventStart) : null;
    const minDate = eventStart && eventStart.isAfter(monthDate.startOf("month")) ? eventStart : monthDate.startOf("month");
    if (monthDate && !now.isSame(monthDate, "month")) {
        if (now.isBefore(minDate)) {
            return minDate.toDate();
        }
        return monthDate.endOf("month").toDate();
    }
    return now.toDate();
}

export const getOtherPropertyGetter = (otherPropPath: string): TGetValueFn<Date> =>
    ({ storage, bindingContext }) => {
        return storage.getValue(bindingContext.getParent().navigate(otherPropPath)) as Date;
    };

export function addEmptyChildLineItem(storage: FormStorage): void {
    const groupDef = storage.data.definition.groups?.find(g => g.id === SocialEventGroup.Child);
    // we have two types of layout -> group layout and FEF layout, so handle different definition
    let columns: IFieldDef[] = [];
    if (groupDef.collection) {
        // group layout collection -> get columns from rows
        columns = groupDef.rows?.flat() ?? [];
    } else {
        columns = groupDef?.lineItems?.columns ?? [];
    }
    // create new lineItem with defaults based on event start date
    const newRow = addEmptyLineItem({
        storage,
        bindingContext: storage.data.bindingContext.navigate(PrMaternityLeaveEventEntity.CareReceivers),
        newItemsCount: 1,
        index: 1,
        columns,
        context: storage.context
    }) as IPrCareReceiverSocialEventEntity;

    storage.setValueByPath(PrMaternityLeaveEventEntity.CareReceivers, [newRow]);
}

export function handleEntitlementChange(storage: FormStorage, e: ISmartFieldChange): void {
    const paths = [PrMaternityLeaveEventEntity.MaternityLeaveEntitlement, PrPaternityLeaveEventEntity.PaternityLeaveEntitlement] as string[];
    if (e.triggerAdditionalTasks && paths.includes(e.bindingContext.getPath())) {
        const groupDef = storage.data.definition.groups?.find(g => g.id === SocialEventGroup.Child);
        const isVisible = getInfoValue(groupDef, "isVisible", { storage });
        if (!isVisible) {
            storage.setValueByPath(PrMaternityLeaveEventEntity.CareReceivers, []);
        } else if (!(storage.data.entity as IPrMaternityLeaveEventEntity).CareReceivers?.length) {
            addEmptyChildLineItem(storage);
        }

        storage.addActiveGroupByKey(SocialEventGroup.Child);
        storage.refresh(true);
    }
}

export function handleDateEventEndDefaultingOnChange(storage: FormStorage, e: ISmartFieldChange, weeks: number): void {
    const path = e.bindingContext.getPath();
    if (path === PrSocialEventEntity.DateEventStart) {
        const newStart = e.value as Date;
        const unit = "weeks";
        const currentStart = storage.getValueByPath(PrSocialEventEntity.DateEventStart) as Date;
        const currentEnd = storage.getValueByPath(PrSocialEventEntity.DateEventEnd) as Date;
        let hasSameDiff = false;
        if (currentStart && currentEnd) {
            const diff = getUtcDayjs(currentEnd).add(1, "day").diff(getUtcDayjs(currentStart), unit);
            hasSameDiff = diff === weeks;
        }
        if (DateType.isValid(newStart) && (!currentEnd || hasSameDiff)) {
            const newEnd = getUtcDayjs(newStart).add(weeks, unit).subtract(1, "day").toDate();
            storage.setValueByPath(PrSocialEventEntity.DateEventEnd, newEnd);
        }
    }
}